import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { emitter } from '@/plugins/emitter'
import { useAuth } from '@/composables/useAuth'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import type { RirekishoFormatData } from '@/composables/format-edit/useRirekishoFormatData'
import type { BasicData } from '@/utils/format-edit/basicData'

type BizreachItem = {
  ig_cd?: string
  i_cd?: string
  jg_cd?: string
  j_cd?: string
  name: string
  category_name: string
} & Record<string, string>

type RequestAiAxiosVm = {
  $axios: {
    post: (
      url: string,
      data: unknown,
      config?: { timeout: number }
    ) => Promise<{
      data: {
        success?: boolean
        message?: string
        industries?: BizreachItem[]
        jobCategories?: BizreachItem[]
        yakushoku?: string
        industry_cd?: string
        job_category_cd?: string
        hope_industry_cd?: string
        hope_job_category_cd?: string
        now_nensyu?: string
        match_method?: Record<string, string>
      }
    }>
  }
}

type Options = {
  formatData: RirekishoFormatData
  basicData: () => BasicData
  emitUpdateBasicData: (payload: Partial<BasicData>) => void
  enabled?: boolean
}

type BizreachGroup = {
  label: string
  options: { value: string; label: string }[]
  ig_cd?: string
  jg_cd?: string
}

function groupByCategory(
  items: BizreachItem[],
  groupKey: 'ig_cd' | 'jg_cd',
  codeKey: 'i_cd' | 'j_cd'
): BizreachGroup[] {
  const groups: Record<string, BizreachGroup> = {}
  items.forEach((item) => {
    const gk = item[groupKey] || ''
    if (!groups[gk]) {
      groups[gk] = { label: item.category_name, options: [] }
      groups[gk][groupKey] = gk
    }
    groups[gk].options.push({
      value: item[codeKey] || '',
      label: item.name
    })
  })
  return Object.values(groups)
}

// フロントのキー → DBカラム名 / match_method のキー
const AI_FIELD_META: Record<string, { column: string; methodKey?: string }> = {
  yakushokuBusho: { column: 'yakushoku_busho' },
  gyoushuCd: { column: 'gyoushu_cd', methodKey: 'industry' },
  shokushuCd: { column: 'shokushu_cd', methodKey: 'job_category' },
  kibouGyoushuCd: { column: 'kibou_gyoushu_cd', methodKey: 'hope_industry' },
  kibouShokushuCd: {
    column: 'kibou_shokushu_cd',
    methodKey: 'hope_job_category'
  },
  nowNensyu: { column: 'nownensyu' }
}

type AiOwnedField =
  | 'yakushokuBusho'
  | 'gyoushuCd'
  | 'shokushuCd'
  | 'kibouGyoushuCd'
  | 'kibouShokushuCd'
  | 'nowNensyu'

export function useRirekishoRequestAi(options: Options) {
  const { vm, varuna } = useHostServices()
  const host = vm as unknown as RequestAiAxiosVm
  const { user, isAuthenticated } = useAuth()
  const enabled = options.enabled !== false

  const requestAiGenerated = ref(false)
  const requestAiLoading = ref(false)
  const requestAiError = ref('')
  const lastAiInputSnapshot = ref<string | null>(null)
  // AIが前回書き込んだ値。本人が手で直した値を再発火で潰さないための出自判定に使う。
  // リロードすると失われるため、保存済みの値は以後すべて本人入力として保護される
  const aiAppliedValues = ref<Partial<Record<AiOwnedField, string>>>({})
  // 保存時にDBへ送る出自ラベル。AIが入れた項目だけを持つ
  const aiEstimatedFields = ref<
    Record<string, { method: string; value: string }>
  >({})
  const bizreachIndustries = ref<BizreachItem[]>([])
  const bizreachJobCategories = ref<BizreachItem[]>([])

  const bizreachIndustryGroups = computed(() =>
    groupByCategory(bizreachIndustries.value, 'ig_cd', 'i_cd')
  )
  const bizreachJobCategoryGroups = computed(() =>
    groupByCategory(bizreachJobCategories.value, 'jg_cd', 'j_cd')
  )

  const industryNameMap = computed(() => {
    const map: Record<string, string> = {}
    bizreachIndustries.value.forEach((i) => {
      if (i.i_cd) map[i.i_cd] = i.name
    })
    return map
  })

  const jobCategoryNameMap = computed(() => {
    const map: Record<string, string> = {}
    bizreachJobCategories.value.forEach((j) => {
      if (j.j_cd) map[j.j_cd] = j.name
    })
    return map
  })

  function getIndustryName(cd: unknown) {
    const key = String(cd ?? '')
    if (!key) return ''
    return industryNameMap.value[key] || key
  }

  function getJobCategoryName(cd: unknown) {
    const key = String(cd ?? '')
    if (!key) return ''
    return jobCategoryNameMap.value[key] || key
  }

  // 保存済みの出自ラベルを内部状態に読み戻す（リロード・バックアップ復元の両方で通る）
  function restoreAiProvenance(basic: BasicData) {
    const saved = basic.aiEstimatedFields
    aiEstimatedFields.value = saved ? { ...saved } : {}
    aiAppliedValues.value = {}
    if (!saved) return
    ;(Object.keys(AI_FIELD_META) as AiOwnedField[]).forEach((key) => {
      const entry = saved[AI_FIELD_META[key].column]
      // 'user' は本人が書いた値。AI由来として扱うと再発火で上書きされてしまう
      if (entry && entry.value && entry.method !== 'user') {
        aiAppliedValues.value[key] = entry.value
      }
    })
  }

  // AIが入れた項目を出自ラベルに記録する（method はサーバの match_method 由来、無ければ 'ai'）
  function markAiEstimated(
    key: AiOwnedField,
    value: string,
    matchMethod?: Record<string, string>
  ) {
    const meta = AI_FIELD_META[key]
    if (!meta) return
    const method =
      (meta.methodKey && matchMethod && matchMethod[meta.methodKey]) || 'ai'
    aiEstimatedFields.value[meta.column] = { method, value }
  }

  // AIが入れた値のままではない非空の項目に 'user' ラベルを付ける。
  // これで「キーが無い = AI未実行または未入力」「'user' = 本人が書いた」と読み分けられる
  function markUserEditedFields() {
    const basic = options.basicData()
    ;(Object.keys(AI_FIELD_META) as AiOwnedField[]).forEach((key) => {
      const column = AI_FIELD_META[key].column
      const current =
        key === 'nowNensyu'
          ? options.formatData.nowNensyu || ''
          : String(basic[key] ?? '')
      if (!current) {
        delete aiEstimatedFields.value[column]
        return
      }
      // AIが入れた値のままならラベルは既にAI側のものが入っている
      if (current === aiAppliedValues.value[key]) return
      aiEstimatedFields.value[column] = { method: 'user', value: current }
    })
  }

  // 空、または前回AIが入れた値のままなら上書きしてよい
  function isAiOwned(key: AiOwnedField, current: string) {
    return !current || current === aiAppliedValues.value[key]
  }

  function buildAiInputSnapshot() {
    return JSON.stringify({
      jobHistories: options.formatData.jobHistories,
      licenseHistories: options.formatData.licenseHistories,
      reason: options.formatData.reason,
      ai_company_name: options.formatData.ai_company_name
    })
  }

  function fetchBizreachMasters() {
    return host.$axios
      .post(varuna.endPoints.bizreachMasters, {}, { timeout: 30000 })
      .then((response) => {
        if (response.data.success) {
          bizreachIndustries.value = response.data.industries || []
          bizreachJobCategories.value = response.data.jobCategories || []
        }
      })
      .catch(() => {})
  }

  function restoreRequestAiState() {
    if (!enabled) return
    const basic = options.basicData()
    // 読み込んだデータに付いている出自ラベルを内部状態へ復元する。
    // ラベルが無い項目・値がラベルと違う項目は本人入力として扱われ、AIに上書きされない
    restoreAiProvenance(basic)
    if (
      basic.yakushokuBusho ||
      basic.gyoushuCd ||
      basic.shokushuCd ||
      basic.kibouGyoushuCd ||
      basic.kibouShokushuCd
    ) {
      requestAiGenerated.value = true
      lastAiInputSnapshot.value = buildAiInputSnapshot()
      if (bizreachIndustries.value.length === 0) {
        fetchBizreachMasters()
      }
    }
  }

  function getAIAutoRequest() {
    if (!enabled) return
    requestAiLoading.value = true
    requestAiError.value = ''
    const basic = options.basicData()
    const postData = {
      uuid: isAuthenticated.value && user.value?.uid ? user.value.uid : '',
      jobHistories: options.formatData.jobHistories,
      licenseHistories: options.formatData.licenseHistories,
      reason: options.formatData.reason,
      nowNensyu: options.formatData.nowNensyu,
      kibouNensyu: options.formatData.kibouNensyu,
      ai_company_name: options.formatData.ai_company_name || '',
      birthYear: basic.birthYear || ''
    }
    host.$axios
      .post(varuna.endPoints.aiAutoRequest, postData, { timeout: 60000 })
      .then((response) => {
        if (response.data.success) {
          const aiFields = {
            yakushokuBusho: response.data.yakushoku,
            gyoushuCd: response.data.industry_cd,
            shokushuCd: response.data.job_category_cd,
            kibouGyoushuCd: response.data.hope_industry_cd,
            kibouShokushuCd: response.data.hope_job_category_cd
          }
          // 値は応答時点で読む（AI応答待ちの間に本人が入力したケースを拾うため）
          const basicNow = options.basicData()
          const payload: Partial<BasicData> = {}
          ;(Object.keys(aiFields) as (keyof typeof aiFields)[]).forEach(
            (key) => {
              const value = aiFields[key]
              // AIが決められなかった項目はキーごと落として既存値を残す
              if (!value) return
              // 本人が入力・変更した値は上書きしない。前回AIが入れた値だけ更新する
              if (!isAiOwned(key, String(basicNow[key] ?? ''))) return
              payload[key] = value
              aiAppliedValues.value[key] = value
              markAiEstimated(key, value, response.data.match_method)
            }
          )
          if (Object.keys(payload).length > 0) {
            options.emitUpdateBasicData(payload)
          }
          // 年収も同じルール（本人が選んだ「700万円以上」を引き下げない）
          if (
            response.data.now_nensyu &&
            isAiOwned('nowNensyu', options.formatData.nowNensyu)
          ) {
            options.formatData.nowNensyu = response.data.now_nensyu
            aiAppliedValues.value.nowNensyu = response.data.now_nensyu
            markAiEstimated('nowNensyu', response.data.now_nensyu)
          }
          // AIが触らなかった項目に本人記入のラベルを付ける
          markUserEditedFields()
          // 出自ラベルは basicData 経由で localStorage → save_backupdata へ運ぶ
          options.emitUpdateBasicData({
            aiEstimatedFields: { ...aiEstimatedFields.value }
          })
          requestAiGenerated.value = true
          lastAiInputSnapshot.value = buildAiInputSnapshot()
        } else {
          requestAiError.value =
            response.data.message || 'AI自動入力に失敗しました。'
        }
      })
      .catch((error: unknown) => {
        const err = error as { response?: { data?: { message?: string } } }
        requestAiError.value =
          (err.response && err.response.data && err.response.data.message) ||
          'AI自動入力でエラーが発生しました。'
      })
      .finally(() => {
        requestAiLoading.value = false
      })
  }

  function runAutoRequestIfNeeded() {
    if (!enabled) return
    const runAi = () => {
      const currentSnapshot = buildAiInputSnapshot()
      if (
        lastAiInputSnapshot.value === null ||
        lastAiInputSnapshot.value !== currentSnapshot
      ) {
        getAIAutoRequest()
      }
    }
    if (bizreachIndustries.value.length === 0) {
      fetchBizreachMasters().then(runAi)
    } else {
      runAi()
    }
  }

  onBeforeMount(() => {
    if (!enabled) return
    emitter.on('local-format-data-loaded', restoreRequestAiState)
  })

  onBeforeUnmount(() => {
    if (!enabled) return
    emitter.off('local-format-data-loaded', restoreRequestAiState)
  })

  return {
    requestAiGenerated,
    requestAiLoading,
    requestAiError,
    bizreachIndustryGroups,
    bizreachJobCategoryGroups,
    getIndustryName,
    getJobCategoryName,
    runAutoRequestIfNeeded
  }
}
